import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Grid,
  IconButton,
  Divider,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Close,
  Edit,
  LocationOn
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks for location selection
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

// Component to recenter map when center prop changes
const RecenterMap: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] !== 0 && center[1] !== 0) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  uuid: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  location: Location;
  elevation?: number;
  notes?: string;
  owner?: string;
  public: boolean;
  source?: string;
  timezone?: string;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  tags?: string[];
}

interface StationEditDialogProps {
  open: boolean;
  onClose: () => void;
  station?: Station;
  onSave: (stationData: Partial<Station>) => Promise<void>;
}

const StationEditDialog: React.FC<StationEditDialogProps> = ({
  open,
  onClose,
  station,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    elevation: '',
    notes: '',
    public: false,
    status: 'active',
    tags: [] as string[]
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([40.7128, -74.0060]); // Default to NYC

  useEffect(() => {
    if (station) {
      setFormData({
        name: station.name || '',
        description: station.description || '',
        latitude: station.location?.latitude?.toString() || '',
        longitude: station.location?.longitude?.toString() || '',
        elevation: station.elevation?.toString() || '',
        notes: station.notes || '',
        public: station.public || false,
        status: station.status || 'active',
        tags: station.tags || []
      });

      // Update map center if station has coordinates
      if (station.location?.latitude && station.location?.longitude) {
        setMapCenter([station.location.latitude, station.location.longitude]);
      }
    }
  }, [station]);

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Station name is required';
    }

    if (!formData.latitude || isNaN(Number(formData.latitude))) {
      newErrors.latitude = 'Latitude is required and must be a number';
    } else {
      const lat = Number(formData.latitude);
      if (lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
    }

    if (!formData.longitude || isNaN(Number(formData.longitude))) {
      newErrors.longitude = 'Longitude is required and must be a number';
    } else {
      const lng = Number(formData.longitude);
      if (lng < -180 || lng > 180) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    if (formData.elevation && isNaN(Number(formData.elevation))) {
      newErrors.elevation = 'Elevation must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const updatedData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: {
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude)
        },
        elevation: formData.elevation ? Number(formData.elevation) : undefined,
        notes: formData.notes.trim() || undefined,
        public: formData.public,
        status: formData.status, // This is already using 'active', 'inactive', 'maintenance'
        tags: formData.tags
      };

      await onSave(updatedData);
      onClose();
    } catch (error) {
      console.error('Error updating station:', error);
      setErrors({ submit: 'Error updating station' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!station) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="primary" />
            <Typography variant="h6">
              Edit Station
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Station Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" />
                Location
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Latitude"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                error={!!errors.latitude}
                helperText={errors.latitude}
                required
                type="number"
                inputProps={{ step: "any" }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Longitude"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                error={!!errors.longitude}
                helperText={errors.longitude}
                required
                type="number"
                inputProps={{ step: "any" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn color="primary" />
                Location - Click on map to update position
              </Typography>
              <Box sx={{ height: '300px', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd', mb: 2 }}>
                <MapContainer
                  center={formData.latitude && formData.longitude ?
                    [Number(formData.latitude), Number(formData.longitude)] : mapCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://cartodb.com/">CartoDB</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    subdomains="abcd"
                    maxZoom={19}
                  />
                  <RecenterMap
                    center={formData.latitude && formData.longitude ?
                      [Number(formData.latitude), Number(formData.longitude)] : mapCenter}
                  />
                  <MapClickHandler onLocationSelect={handleMapLocationSelect} />
                  {formData.latitude && formData.longitude && (
                    <Marker
                      position={[Number(formData.latitude), Number(formData.longitude)]}
                    />
                  )}
                </MapContainer>
              </Box>
              <Box sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mb: 2
              }}>
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 'medium' }}>
                  Current coordinates: {formData.latitude && formData.longitude ?
                    `${Number(formData.latitude).toFixed(6)}, ${Number(formData.longitude).toFixed(6)}` :
                    'Not set'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Elevation (meters)"
                value={formData.elevation}
                onChange={(e) => setFormData({ ...formData, elevation: e.target.value })}
                error={!!errors.elevation}
                helperText={errors.elevation}
                type="number"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                select
                SelectProps={{ native: true }}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="debug">Debug</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.public}
                    onChange={(e) => setFormData({ ...formData, public: e.target.checked })}
                  />
                }
                label="Public Station"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, tags: newValue });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags (press Enter to add)"
                    helperText="Type and press Enter to add tags for grouping stations"
                  />
                )}
              />
            </Grid>

            {/* Read-only fields for information */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography variant="subtitle2" gutterBottom>
                  Read-only fields:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>UUID:</strong> {station.uuid}
                </Typography>
                {station.source && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Source:</strong> {station.source}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Created:</strong> {new Date(station.created_at).toLocaleDateString('en-US')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={<Edit />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StationEditDialog;